import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useApp } from '@/contexts/AppContext';
import { t as tRaw } from '@/locales/strings';
import { hapticTap } from '@/utils/haptics';

// ── React replacement for the vanilla s-gate paywall ──────────────────
//
// Rendered by AppShell when the authenticated user's profiles.is_active
// is false. Three Stripe-linked plan cards + a sign-out link. Matches
// the Stripe URL resolution pattern from src/main.js (hydrateStripeGateLinks):
// URLs come from VITE_STRIPE_{FOUNDATION,EMBODIMENT,INNER_CIRCLE}_URL.
// Missing env → clicking the card opens a polite alert instead of
// navigating to a broken checkout.

const STRIPE_URLS = {
  // Single membership checkout. Falls back to the legacy tier URLs so an
  // existing Stripe setup keeps working until VITE_STRIPE_MEMBERSHIP_URL is set.
  membership:
    import.meta.env.VITE_STRIPE_MEMBERSHIP_URL ||
    import.meta.env.VITE_STRIPE_EMBODIMENT_URL ||
    import.meta.env.VITE_STRIPE_FOUNDATION_URL ||
    '',
};

// Single membership. Localised copy lives in strings.js under
// gate_tier_membership_{title|desc}. price is a display string only — it
// MUST match the amount and billing interval set on the Stripe product
// behind VITE_STRIPE_MEMBERSHIP_URL.
const PLANS = [
  { key: 'membership', variant: 'default', price: '$19' },
];

const s = {
  screen: {
    position: 'fixed',
    inset: 0,
    zIndex: 9998,
    background: 'var(--cream, #FAF6F1)',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    paddingTop: 'env(safe-area-inset-top, 0px)',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  },
  scroll: {
    flex: 1,
    width: '100%',
    maxWidth: 460,
    margin: '0 auto',
    padding: '32px 24px 40px',
    boxSizing: 'border-box',
  },
  wordmark: {
    display: 'block',
    textAlign: 'center',
    fontFamily: "var(--font-title, 'Playfair Display', Georgia, serif)",
    fontStyle: 'italic',
    fontWeight: 500,
    fontSize: 44,
    lineHeight: 1,
    color: 'var(--primary, #A8003F)',
    letterSpacing: '-0.01em',
    margin: '12px 0 24px',
  },
  title: {
    fontFamily: "var(--font-title, 'Playfair Display', Georgia, serif)",
    fontSize: 32,
    fontWeight: 500,
    fontStyle: 'italic',
    color: 'var(--dark-brown, #5A3E2B)',
    lineHeight: 1.2,
    margin: '0 0 8px',
    textAlign: 'center',
  },
  sub: {
    fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
    fontSize: 14,
    color: 'var(--warm-brown, #8B7355)',
    lineHeight: 1.5,
    margin: '0 0 28px',
    textAlign: 'center',
  },
  cards: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 24,
  },
  card: {
    position: 'relative',
    width: '100%',
    display: 'block',
    textDecoration: 'none',
    textAlign: 'left',
    background: '#FFFFFF',
    border: '1px solid var(--border, #E8DDD4)',
    borderRadius: 'var(--radius-lg, 16px)',
    padding: '22px 22px 22px',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    transition: 'transform 140ms ease, border-color 150ms ease, box-shadow 220ms ease',
    boxShadow: '0 1px 2px rgba(45,26,31,0.04), 0 10px 24px -18px rgba(45,26,31,0.18)',
  },
  cardFeatured: {
    background: 'linear-gradient(180deg, #FFFFFF 0%, var(--primary-pale, #F5D6E0) 220%)',
    border: '1.5px solid var(--primary, #A8003F)',
    boxShadow: '0 1px 2px rgba(168,0,63,0.08), 0 22px 44px -22px rgba(168,0,63,0.32)',
    paddingTop: 30,
  },
  cardDark: {
    background: 'linear-gradient(135deg, #2D1A1F 0%, #4A2630 60%, #6F3A48 100%)',
    border: '1px solid rgba(255,255,255,0.06)',
    color: '#FAF6F1',
    boxShadow: '0 1px 2px rgba(0,0,0,0.12), 0 22px 44px -22px rgba(45,26,31,0.4)',
  },
  tier: {
    fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: 'var(--primary-dark, var(--primary, #A8003F))',
    marginBottom: 8,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  tierOnDark: {
    color: 'rgba(255,220,236,0.92)',
  },
  amount: {
    fontFamily: "var(--font-title, 'Playfair Display', Georgia, serif)",
    fontSize: 36,
    fontWeight: 500,
    color: 'var(--dark-brown, #5A3E2B)',
    lineHeight: 1.05,
    marginBottom: 10,
    fontVariationSettings: '"opsz" 96',
    letterSpacing: '-0.015em',
  },
  amountOnDark: {
    color: '#FAF6F1',
  },
  unit: {
    fontSize: 13,
    fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
    color: 'var(--warm-brown, #8B7355)',
    marginLeft: 6,
    fontWeight: 500,
    letterSpacing: '0.02em',
  },
  desc: {
    fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
    fontSize: 14,
    color: 'var(--warm-brown, #8B7355)',
    lineHeight: 1.5,
    marginBottom: 14,
  },
  descOnDark: {
    color: 'rgba(250, 246, 241, 0.78)',
  },
  ribbon: {
    position: 'absolute',
    top: -10,
    left: 22,
    background: 'linear-gradient(180deg, #B8094A 0%, var(--primary) 100%)',
    color: '#fff',
    fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
    fontSize: 9.5,
    fontWeight: 800,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    padding: '5px 11px',
    borderRadius: 999,
    boxShadow: '0 4px 12px -4px rgba(168,0,63,0.45), inset 0 1px 0 rgba(255,255,255,0.22)',
  },
  cta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'var(--primary)',
  },
  ctaOnDark: {
    color: '#FFD9DF',
  },
  warning: {
    fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
    fontSize: 12,
    color: '#B91C1C',
    textAlign: 'center',
    marginTop: -8,
    marginBottom: 16,
    padding: '0 12px',
    lineHeight: 1.4,
  },
  signout: {
    display: 'block',
    margin: '24px auto 0',
    padding: '12px 24px',
    background: 'transparent',
    border: 'none',
    fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--warm-brown, #8B7355)',
    textDecoration: 'underline',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
};

export default function MembershipGate() {
  const { lang, refreshProfile } = useApp();
  const [alertMsg, setAlertMsg] = useState('');
  const [checking, setChecking] = useState(false);
  // Sticky after a Stripe tab was opened. Has to be state, not a
  // ref, because the conditional render of the "I've completed
  // payment" CTA depends on it — refs don't trigger re-renders so
  // using one would leave the button invisible.
  const [openedCheckout, setOpenedCheckout] = useState(false);

  // Strip any vanilla screen still in .on. Mirrors the defensive sweep
  // in AuthScreen — if the legacy s-gate was already painting, we
  // hide it so React is the only paywall surface.
  useEffect(() => {
    document.querySelectorAll('.screen.on').forEach((el) => el.classList.remove('on'));
    const loader = document.getElementById('app-loading');
    if (loader) loader.style.display = 'none';
  }, []);

  // Refetch the profile when the user returns to the app from the
  // Stripe checkout tab. visibilitychange fires on tab switch; focus
  // covers the case of a same-tab redirect. We don't loop — a single
  // refetch per return is enough; AppShell will unmount this screen
  // the moment is_active flips to true.
  useEffect(() => {
    if (!refreshProfile) return;
    if (!openedCheckout) return;
    let cancelled = false;
    async function maybeRefetch() {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      setChecking(true);
      try { await refreshProfile(); } catch { /* soft gate — any failure leaves us here */ }
      if (!cancelled) setChecking(false);
    }
    window.addEventListener('focus', maybeRefetch);
    document.addEventListener('visibilitychange', maybeRefetch);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', maybeRefetch);
      document.removeEventListener('visibilitychange', maybeRefetch);
    };
  }, [refreshProfile, openedCheckout]);

  async function handleManualRefresh() {
    if (checking) return;
    hapticTap();
    setChecking(true);
    try { await refreshProfile(); } catch { /* soft */ }
    setChecking(false);
  }

  function handlePlanClick(planKey) {
    hapticTap();
    const url = STRIPE_URLS[planKey];
    if (!url) {
      setAlertMsg(tRaw('gate_checkout_unconfigured', lang));
      return;
    }
    setOpenedCheckout(true);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async function handleSignOut() {
    if (!supabase) return;
    try { await supabase.auth.signOut(); } catch { /* non-fatal */ }
    // AppContext.onAuthStateChange fires SIGNED_OUT → user becomes
    // null → AppShell renders AuthScreen. No redirect needed.
  }

  return (
    <div style={s.screen} role="dialog" aria-labelledby="gate-title">
      <div style={s.scroll}>
        <span style={s.wordmark} role="banner" aria-label="Be Bold">Be Bold</span>
        <h1 id="gate-title" style={s.title}>{tRaw('gate_title', lang)}</h1>
        <p style={s.sub}>{tRaw('gate_sub', lang)}</p>

        {alertMsg && (
          <div role="alert" style={s.warning}>{alertMsg}</div>
        )}

        <div style={s.cards}>
          {PLANS.map((plan) => {
            const isDark = plan.variant === 'dark';
            const isFeatured = plan.variant === 'featured';
            const cardStyle = {
              ...s.card,
              ...(isFeatured ? s.cardFeatured : null),
              ...(isDark ? s.cardDark : null),
            };
            const tierStyle = { ...s.tier, ...(isDark ? s.tierOnDark : null) };
            const amountStyle = { ...s.amount, ...(isDark ? s.amountOnDark : null) };
            const descStyle = { ...s.desc, ...(isDark ? s.descOnDark : null) };
            return (
              <button
                key={plan.key}
                type="button"
                style={cardStyle}
                className="tap-card"
                onClick={() => handlePlanClick(plan.key)}
                aria-label={tRaw(`gate_tier_${plan.key}_title`, lang)}
              >
                {/* "MOST POPULAR" ribbon for the featured tier — sits
                    half-bleed over the top edge of the card so the
                    eye lands on it before the price. */}
                {isFeatured && (
                  <span style={s.ribbon}>{tRaw('gate_most_popular', lang)}</span>
                )}
                {/* Subtle highlight in the top-right of the dark card
                    so the deep gradient feels lit, not flat. */}
                {isDark && (
                  <span aria-hidden="true" style={{
                    position: 'absolute',
                    top: -50, right: -40,
                    width: 180, height: 180,
                    borderRadius: '50%',
                    background: 'radial-gradient(closest-side, rgba(255,255,255,0.10), transparent 70%)',
                    pointerEvents: 'none',
                  }} />
                )}
                <div style={tierStyle}>
                  <span style={{
                    width: 5, height: 5, borderRadius: '50%',
                    background: isDark ? '#FFD9DF' : 'var(--primary)',
                  }} aria-hidden="true" />
                  {tRaw(`gate_tier_${plan.key}_title`, lang)}
                </div>
                <div style={amountStyle}>
                  {plan.price}
                  <span style={{ ...s.unit, color: isDark ? 'rgba(250,246,241,0.65)' : s.unit.color }}>
                    {tRaw('gate_per_month', lang)}
                  </span>
                </div>
                <div style={descStyle}>
                  {tRaw(`gate_tier_${plan.key}_desc`, lang)}
                </div>
                <span style={{ ...s.cta, ...(isDark ? s.ctaOnDark : null) }}>
                  {tRaw('gate_choose_plan', lang)}
                  <span aria-hidden="true" style={{ fontSize: 16, fontWeight: 400 }}>→</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Manual refresh — shown only once the user has opened a
            Stripe checkout tab. The auto-refetch on visibilitychange
            handles the common case; this button covers iOS Safari and
            anything else where the event doesn't fire reliably. */}
        {openedCheckout && (
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={checking}
            aria-busy={checking}
            style={{
              display: 'block',
              width: '100%',
              margin: '4px 0 0',
              padding: '14px 20px',
              background: 'transparent',
              border: '1.5px solid var(--primary, #A8003F)',
              borderRadius: 999,
              fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
              fontSize: 13,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.10em',
              color: 'var(--primary, #A8003F)',
              cursor: checking ? 'not-allowed' : 'pointer',
              opacity: checking ? 0.6 : 1,
              WebkitTapHighlightColor: 'transparent',
              minHeight: 48,
            }}
          >
            {checking ? tRaw('auth_one_moment', lang) : tRaw('gate_check_again', lang)}
          </button>
        )}

        <button
          type="button"
          style={s.signout}
          onClick={handleSignOut}
        >
          {tRaw('gate_signout', lang)}
        </button>
      </div>
    </div>
  );
}
